import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pause, Play, X } from 'lucide-react';
import { useAuth } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PausedAccountBanner — sticky top strip shown when the logged-in user
 * has an active 30-day pause. Provides a one-tap "Unpause Now" action.
 *
 * Hidden when no user, when user is not paused, or when the user dismisses it
 * (dismissal persists for the current tab only via sessionStorage).
 */
const PausedAccountBanner = () => {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState({ paused: false, days_left: 0, paused_until: null });
  const [unpausing, setUnpausing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/users/me/pause-status`);
      setStatus(data);
    } catch {
      // silently ignore
    }
  }, [user]);

  useEffect(() => {
    // Restore per-session dismissal state
    try {
      const d = sessionStorage.getItem('pause-banner-dismissed');
      if (d === '1') setDismissed(true);
    } catch {
      // noop
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleUnpause = async () => {
    setUnpausing(true);
    try {
      await axios.post(`${API}/users/me/unpause`);
      setStatus({ paused: false, days_left: 0, paused_until: null });
      toast.success('Welcome back — your account is active again.');
      // Refresh the global user object so downstream UI updates too
      if (typeof refreshUser === 'function') {
        try { await refreshUser(); } catch { /* noop */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpause account');
    } finally {
      setUnpausing(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('pause-banner-dismissed', '1'); } catch { /* noop */ }
  };

  if (!user || !status.paused || dismissed) return null;

  const resumeDate = status.paused_until
    ? new Date(status.paused_until).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : '';

  return (
    <div
      className="sticky top-0 z-[60] w-full bg-amber-500 text-white shadow-md"
      data-testid="paused-account-banner"
      role="alert"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <Pause className="w-4 h-4 shrink-0" />
          <span className="text-sm font-medium truncate">
            <span className="hidden sm:inline">Your account is paused — </span>
            <strong>{status.days_left}</strong>{' '}
            {status.days_left === 1 ? 'day' : 'days'} left
            <span className="hidden md:inline">, auto-resumes on {resumeDate}</span>.
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={handleUnpause}
            disabled={unpausing}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/95 text-amber-700 hover:bg-white px-3 py-1 text-xs font-bold transition disabled:opacity-60"
            data-testid="banner-unpause-btn"
          >
            <Play className="w-3.5 h-3.5" />
            {unpausing ? 'Unpausing…' : 'Unpause Now'}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded-full p-1 hover:bg-white/10 transition"
            aria-label="Dismiss for this session"
            data-testid="banner-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PausedAccountBanner;
